import {
  DropDownButton,
  type DropDownButtonProps,
} from '@progress/kendo-react-buttons'
import { Loader } from '@progress/kendo-react-indicators'
import {
  Checkbox,
  Switch,
  type SwitchProps,
} from '@progress/kendo-react-inputs'
import { Tooltip } from '@progress/kendo-react-tooltip'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import { Icon } from '~/components/elements/Icon'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledButton } from '~/components/elements/StyledButton'
import { StyledInput } from '~/components/elements/StyledInput'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { BrandGroupSelector } from '~/components/features/BrandGroupSelector'
import { CustomerAttributeGroupSelector } from '~/components/features/CustomerAttributeGroupSelector'
import { QuestionTreeSelector } from '~/components/features/QuestionTreeSelector'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryAvailableBrandGroups } from '~/hooks/useSurveyQuery/useSurveyQueryAvailableBrandGroups'
import { useSurveyQueryCustomerAttributeGroups } from '~/hooks/useSurveyQuery/useSurveyQueryCustomerAttributeGroups'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery/useSurveyQueryData'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery/useSurveyQueryQuestions'
import { compact } from '~/utils'

import {
  activeCustomRoles,
  BUILTIN_QUESTION_ROLES,
  type BuiltinQuestionRole,
  CUSTOM_QUESTION_ROLES,
  type CustomRoleAxis,
  questionRoleDescriptions,
  questionRoleLabels,
} from '../atoms'
import { useBasicOutputWizard, useMatrixQuestionValidation } from '../hooks'

// マトリクス設問（親または子）のみ選択を許可するロール
const MATRIX_REQUIRED_ROLES: ReadonlySet<string> = new Set([
  'npiDriver', // NPIドライバー設問
  'trigger', // きっかけ設問
  'resistance', // 抵抗設問
])

// タクソノミの4項目（設問選択（NPIドライバー／重視点／きっかけ／抵抗）をする）に対応
const BUILTIN_QUESTION_ROLE_LOG_EVENT_NAMES: Record<
  BuiltinQuestionRole,
  string
> = {
  npiDriver: 'Select NPI driver question',
  importance: 'Select importance question',
  trigger: 'Select trigger question',
  resistance: 'Select resistance question',
}

type SurveyContextStepProps = {
  aiEnabled: boolean
  effectiveAiOn: boolean
  isAiStateResolved: boolean
  onAiOnChange: (next: boolean) => void
  onNext: () => void
}

export const SurveyContextStep = ({
  aiEnabled,
  effectiveAiOn,
  isAiStateResolved,
  onAiOnChange,
  onNext,
}: SurveyContextStepProps) => {
  const survey = useSurveyQueryData()
  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const questions = useSurveyQueryQuestions()
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()
  const { t } = useTranslation()
  const logEvent = useLogEvent()
  const {
    addCustomRole,
    brandGroupId,
    customLabels,
    getBrandAligned,
    getCustomRoleAxis,
    hasAnyQuestionSelected,
    isSurveyContextValid,
    questionAssignments,
    removeCustomRole,
    setBrandAligned,
    setBrandGroupId,
    setCustomLabel,
    setQuestionAssignments,
  } = useBasicOutputWizard()

  const handleAiOnChange = React.useCallback<EH<SwitchProps, 'onChange'>>(
    (event) => {
      onAiOnChange(event.target.value)
      logEvent('Check ai comment generation', {
        eventProperties: [
          { key: 'checked', value: String(event.target.value) },
        ],
      })
    },
    [logEvent, onAiOnChange],
  )

  const customRoles = activeCustomRoles(questionAssignments)

  // 空のグループを選べると、ステップ1の検証は通るのに送信時にサーバへ弾かれる
  const selectableCustomerAttributeGroups = React.useMemo(
    () =>
      customerAttributeGroups.filter(
        ({ customerAttributes }) => 0 < customerAttributes.length,
      ),
    [customerAttributeGroups],
  )

  const addCustomRoleItems = React.useMemo<
    { label: string; value: CustomRoleAxis }[]
  >(
    () =>
      compact([
        {
          label: t('select_from_questionnaire_response_2'),
          value: 'question',
        },
        0 < selectableCustomerAttributeGroups.length && {
          label: t('select_from_customized_segments_2'),
          value: 'customerAttribute',
        },
      ]),
    [selectableCustomerAttributeGroups.length, t],
  )

  const handleAddCustomRoleItemClick = React.useCallback<
    EH<DropDownButtonProps, 'onItemClick'>
  >(
    (event) => {
      addCustomRole(event.item.value as CustomRoleAxis)
      logEvent('Add custom question')
    },
    [addCustomRole, logEvent],
  )

  const brandCount = React.useMemo(() => {
    if (brandGroupId === null) {
      return 0
    }
    const brandGroup = brandGroups.find(({ id }) => id === brandGroupId)

    return brandGroup?.brands.length ?? 0
  }, [brandGroupId, brandGroups])

  const classifyMatrixSlot = (questionId: string | null) => {
    if (!questionId || brandCount === 0) {
      return null
    }
    const q = questions.find((qq) => qq.question_id === questionId)
    if (!q || q.mt_questions.length === 0) {
      return null
    }

    return {
      canBrandAlign: q.mt_questions.length === brandCount,
    }
  }

  const { getMatrixRequiredError, hasMatrixError } =
    useMatrixQuestionValidation(MATRIX_REQUIRED_ROLES)

  return (
    <VStack spacing={4}>
      <LabelingContainer label={t('basic_output_survey_label')}>
        <Typography variant="normal">{survey?.title ?? ''}</Typography>
      </LabelingContainer>

      <LabelingContainer label={t('basic_output_brand_group_label')}>
        <BrandGroupSelector
          brandGroups={brandGroups}
          unsetText={t('basic_output_brand_group_unset')}
          value={brandGroupId}
          width={360}
          onChange={(value) => {
            setBrandGroupId(value)
            logEvent('Select brand group')
          }}
        />
      </LabelingContainer>

      {BUILTIN_QUESTION_ROLES.map((role) => {
        const selectedId = questionAssignments[role]?.[0] ?? null
        const matrix = classifyMatrixSlot(selectedId)
        const brandAligned = getBrandAligned(role)
        const isFullCombination =
          matrix !== null && (!matrix.canBrandAlign || !brandAligned)
        const isMatrixRequiredError = getMatrixRequiredError(role, selectedId)

        return (
          <div key={role}>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: '4px',
                marginBottom: '4px',
              }}
            >
              <Typography variant="normal">
                {t(questionRoleLabels[role])}
              </Typography>
              <Tooltip anchorElement="target" openDelay={0} position="right">
                <Icon
                  icon="help"
                  size="1rem"
                  title={t(questionRoleDescriptions[role])}
                />
              </Tooltip>
            </div>
            <QuestionTreeSelector
              isMatrixQuestionSelectable={true}
              isNullSelectable={true}
              nullText={t('basic_output_question_deselect')}
              questions={questions}
              unsetText={t('basic_output_question_unset')}
              value={selectedId}
              onChange={(questionId) => {
                setQuestionAssignments({
                  ...questionAssignments,
                  [role]: questionId ? [questionId] : [],
                })
                if (questionId) {
                  logEvent(BUILTIN_QUESTION_ROLE_LOG_EVENT_NAMES[role])
                }
              }}
            />
            {isMatrixRequiredError && (
              <Typography color="error" variant="small">
                {t('basic_output_matrix_required_error')}
              </Typography>
            )}
            {matrix !== null && matrix.canBrandAlign && (
              <div style={{ marginTop: '4px' }}>
                <Checkbox
                  checked={brandAligned}
                  label={t('basic_output_brand_aligned_checkbox')}
                  onChange={(e) => {
                    setBrandAligned(role, e.value)
                    logEvent('Check brand aligned', {
                      eventProperties: [
                        { key: 'checked', value: String(e.value) },
                      ],
                    })
                  }}
                />
              </div>
            )}
            {isFullCombination && (
              <Typography color="error" variant="small">
                {t('basic_output_matrix_warning')}
              </Typography>
            )}
          </div>
        )
      })}

      <LabelingContainer label={t('basic_output_custom_question_section')}>
        {customRoles.map((role) => {
          const selectedId = questionAssignments[role]?.[0] ?? null
          const axis = getCustomRoleAxis(role)
          const isCustomerAttributeAxis = axis === 'customerAttribute'
          // 顧客属性軸の selectedId は設問IDではないので設問として引かない
          const matrix = isCustomerAttributeAxis
            ? null
            : classifyMatrixSlot(selectedId)
          const brandAligned = getBrandAligned(role)
          const isFullCombination =
            matrix !== null && (!matrix.canBrandAlign || !brandAligned)

          return (
            <div key={role} style={{ marginBottom: '16px' }}>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '4px',
                }}
              >
                <StyledInput
                  placeholder={t(
                    'basic_output_custom_question_name_placeholder',
                  )}
                  style={{ width: '240px' }}
                  value={customLabels[role] ?? ''}
                  onValueChange={(value) => setCustomLabel(role, value)}
                />
                <StyledButton
                  buttonStyle="normal"
                  onClick={() => removeCustomRole(role)}
                >
                  {t('basic_output_remove')}
                </StyledButton>
              </div>
              {/* 選択後もどちらの軸の枠か分かるように、絞り込み条件UIと同じ文言で見出しを出す */}
              <div style={{ marginBottom: '4px' }}>
                <Typography variant="normal">
                  {t(
                    isCustomerAttributeAxis
                      ? 'customized_segments_group_3'
                      : 'questionnaire_4',
                  )}
                </Typography>
              </div>
              {isCustomerAttributeAxis ? (
                <CustomerAttributeGroupSelector
                  customerAttributeGroups={selectableCustomerAttributeGroups}
                  isNullSelectable={true}
                  nullText={t('basic_output_question_deselect')}
                  unsetText={t('basic_output_customer_attribute_group_unset')}
                  value={selectedId}
                  onChange={(customerAttributeGroupId) => {
                    setQuestionAssignments({
                      ...questionAssignments,
                      [role]: customerAttributeGroupId
                        ? [customerAttributeGroupId]
                        : [],
                    })
                  }}
                />
              ) : (
                <QuestionTreeSelector
                  isMatrixQuestionSelectable={true}
                  isNullSelectable={true}
                  nullText={t('basic_output_question_deselect')}
                  questions={questions}
                  unsetText={t('basic_output_question_unset')}
                  value={selectedId}
                  onChange={(questionId) => {
                    setQuestionAssignments({
                      ...questionAssignments,
                      [role]: questionId ? [questionId] : [],
                    })
                  }}
                />
              )}
              {matrix !== null && matrix.canBrandAlign && (
                <div style={{ marginTop: '4px' }}>
                  <Checkbox
                    checked={brandAligned}
                    label={t('basic_output_brand_aligned_checkbox')}
                    onChange={(e) => {
                      setBrandAligned(role, e.value)
                      logEvent('Check brand aligned', {
                        eventProperties: [
                          { key: 'checked', value: String(e.value) },
                        ],
                      })
                    }}
                  />
                </div>
              )}
              {isFullCombination && (
                <Typography color="error" variant="small">
                  {t('basic_output_matrix_warning')}
                </Typography>
              )}
            </div>
          )
        })}
        <DropDownButton
          disabled={CUSTOM_QUESTION_ROLES.length <= customRoles.length}
          icon="plus-outline"
          items={addCustomRoleItems}
          text={t('basic_output_custom_question_add')}
          textField="label"
          onItemClick={handleAddCustomRoleItemClick}
        />
      </LabelingContainer>

      <LabelingContainer label={t('confirmation_ai_toggle_label')}>
        {isAiStateResolved ? (
          <VStack spacing={1}>
            <Switch
              checked={effectiveAiOn}
              disabled={!aiEnabled}
              offLabel={t('off')}
              onChange={handleAiOnChange}
              onLabel={t('on')}
            />
            {!effectiveAiOn && (
              <Typography
                color={!aiEnabled ? 'error' : 'light'}
                variant="small"
              >
                {!aiEnabled
                  ? t('ai_disabled_message')
                  : t('confirmation_ai_off_hint')}
              </Typography>
            )}
          </VStack>
        ) : (
          // Query still loading (or errored — the wizard's error banner above
          // already explains that case). aiEnabled/effectiveAiOn default to
          // false while unresolved, so rendering the switch here would show a
          // false "AI off" state instead of the actual loading state.
          <Loader size="small" type="infinite-spinner" />
        )}
      </LabelingContainer>

      {!hasAnyQuestionSelected && (
        <Typography color="error" variant="small">
          {t('basic_output_no_question_selected_warning')}
        </Typography>
      )}

      <HStack spacing={2}>
        <StyledButton
          buttonStyle="success"
          disabled={!isSurveyContextValid || hasMatrixError}
          onClick={onNext}
        >
          {t('basic_output_next')}
        </StyledButton>
      </HStack>
    </VStack>
  )
}
